"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { OTPInput } from "input-otp";
import { Check, ChevronDown } from "lucide-react";
import "./form.css";

/*
 * THE v2 FORM KIT — the first piece of it, born on /settings and meant for
 * every form after it (create-nft, auctions, drops…). A field is a label, a
 * control in one shell (an icon or a fixed prefix may sit inside it), and one
 * line under it that is either the hint or the error — never both. Locked
 * (view-mode) fields read as text on paper, not as greyed inputs.
 */

export function Field(t0) {
  const $ = _c(16);
  const {
    id,
    label,
    required,
    hint,
    error,
    children,
    wide
  } = t0;
  const t1 = wide || undefined;
  const t2 = error ? true : undefined;
  let t3;
  if ($[0] !== required) {
    t3 = required ? <span className="ff-req" aria-hidden={true}>*</span> : null;
    $[0] = required;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== id || $[3] !== label || $[4] !== t3) {
    t4 = <label htmlFor={id} className="ff-label">{label}{t3}</label>;
    $[2] = id;
    $[3] = label;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== error || $[7] !== hint || $[8] !== id) {
    t5 = error ? <p id={`${id}-msg`} className="ff-msg" data-tone="error" role="alert">{error}</p> : hint ? <p id={`${id}-msg`} className="ff-msg">{hint}</p> : null;
    $[6] = error;
    $[7] = hint;
    $[8] = id;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== children || $[11] !== t1 || $[12] !== t2 || $[13] !== t4 || $[14] !== t5) {
    t6 = <div className="ff" data-wide={t1} data-error={t2}>{t4}{children}{t5}</div>;
    $[10] = children;
    $[11] = t1;
    $[12] = t2;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
}

/** The control's shell. `ltr` for a value written left to right (an address, a handle, a number): its
    prefix — «+98», «@», «https://» — then sits before it in either language. */
export function Shell(t0) {
  const $ = _c(11);
  const {
    lead,
    trail,
    children,
    locked,
    multiline,
    ltr
  } = t0;
  const t1 = ltr ? "ltr" : undefined;
  const t2 = locked || undefined;
  const t3 = multiline || undefined;
  let t4;
  if ($[0] !== lead) {
    t4 = lead ? <span className="ff-lead">{lead}</span> : null;
    $[0] = lead;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] !== trail) {
    t5 = trail ? <span className="ff-trail">{trail}</span> : null;
    $[2] = trail;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== children || $[5] !== t1 || $[6] !== t2 || $[7] !== t3 || $[8] !== t4 || $[9] !== t5) {
    t6 = <div className="ff-shell" dir={t1} data-locked={t2} data-multi={t3}>{t4}{children}{t5}</div>;
    $[4] = children;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
}

/** A switch: a real `role="switch"` button, the thumb in the accent when on. */
export function Switch(t0) {
  const $ = _c(9);
  const {
    checked,
    onChange,
    disabled,
    label
  } = t0;
  let t1;
  if ($[0] !== checked || $[1] !== onChange) {
    t1 = () => onChange(!checked);
    $[0] = checked;
    $[1] = onChange;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span aria-hidden={true} />;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== checked || $[5] !== disabled || $[6] !== label || $[7] !== t1) {
    t3 = <button type="button" role="switch" aria-checked={checked} aria-label={label} disabled={disabled} className="ff-switch v2-focus" onClick={t1}>{t2}</button>;
    $[4] = checked;
    $[5] = disabled;
    $[6] = label;
    $[7] = t1;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}

/** A section a switch opens — locked content, list for sale: the row, then its fields while on. */
export function ToggleSection(t0) {
  const $ = _c(20);
  const {
    title,
    hint,
    checked,
    onChange,
    children
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <b>{title}</b>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== hint) {
    t2 = hint ? <small>{hint}</small> : null;
    $[2] = hint;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div>{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== checked || $[8] !== onChange || $[9] !== title) {
    t4 = <Switch checked={checked} onChange={onChange} label={title} />;
    $[7] = checked;
    $[8] = onChange;
    $[9] = title;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t3 || $[12] !== t4) {
    t5 = <div className="ff-toggle-row">{t3}{t4}</div>;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== checked || $[15] !== children) {
    t6 = checked ? <div className="ff-toggle-body">{children}</div> : null;
    $[14] = checked;
    $[15] = children;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t5 || $[18] !== t6) {
    t7 = <div className="ff-toggle">{t5}{t6}</div>;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  return t7;
}
export type PickOption = {
  value: string;
  label: string;
  lead?: ReactNode;
  hint?: string;
};

/**
 * A choice from a short list, in the sort menu's grammar (gold tick and spine
 * on the chosen one) — never a native <select>. Its panel hangs from the
 * trigger in place, not portalled, so it also works inside a <dialog>.
 */
export function Picker(t0) {
  const $ = _c(33);
  const {
    id,
    value,
    options,
    onChange,
    label,
    disabled,
    block,
    placeholder,
    invalid,
    empty
  } = t0;
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  let t1;
  if ($[0] !== options || $[1] !== value) {
    let t2;
    if ($[3] !== value) {
      t2 = o => o.value === value;
      $[3] = value;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = options.find(t2);
    $[0] = options;
    $[1] = value;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const current = t1;
  let t2;
  let t3;
  if ($[5] !== open) {
    t2 = () => {
      if (!open) {
        return;
      }
      const close = e => {
        if (!box.current?.contains(e.target as Node)) {
          setOpen(false);
        }
      };
      const esc = e_0 => {
        if (e_0.key === "Escape") {
          setOpen(false);
        }
      };
      document.addEventListener("mousedown", close);
      document.addEventListener("keydown", esc);
      return () => {
        document.removeEventListener("mousedown", close);
        document.removeEventListener("keydown", esc);
      };
    };
    t3 = [open];
    $[5] = open;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  useEffect(t2, t3);
  const t4 = block || undefined;
  const t5 = invalid || undefined;
  const t6 = id ? `${id}-msg` : undefined;
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => setOpen(_temp);
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const t8 = current?.lead;
  const t9 = current ? undefined : true;
  const t10 = current?.label ?? placeholder ?? "\u2014";
  let t11;
  if ($[9] !== t10 || $[10] !== t9) {
    t11 = <span data-placeholder={t9}>{t10}</span>;
    $[9] = t10;
    $[10] = t9;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  let t12;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <ChevronDown aria-hidden={true} />;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  let t13;
  if ($[13] !== disabled || $[14] !== id || $[15] !== label || $[16] !== open || $[17] !== t11 || $[18] !== t5 || $[19] !== t6 || $[20] !== t8) {
    t13 = <button id={id} type="button" className="ff-picker-btn v2-focus" aria-haspopup="listbox" aria-expanded={open} aria-label={label} aria-invalid={t5} aria-describedby={t6} disabled={disabled} onClick={t7}>{t8}{t11}{t12}</button>;
    $[13] = disabled;
    $[14] = id;
    $[15] = label;
    $[16] = open;
    $[17] = t11;
    $[18] = t5;
    $[19] = t6;
    $[20] = t8;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== empty || $[23] !== label || $[24] !== onChange || $[25] !== open || $[26] !== options || $[27] !== value) {
    t14 = open ? <ul className="ff-picker-list" role="listbox" aria-label={label}>{options.length === 0 && empty ? <li className="ff-picker-empty">{empty}</li> : null}{options.map(o_0 => <li key={o_0.value}><button type="button" role="option" aria-selected={o_0.value === value} className="v2-focus" onClick={() => {
          onChange(o_0.value);
          setOpen(false);
        }}>{o_0.lead}<span>{o_0.label}</span>{o_0.hint ? <small>{o_0.hint}</small> : null}{o_0.value === value ? <Check aria-hidden={true} /> : null}</button></li>)}</ul> : null;
    $[22] = empty;
    $[23] = label;
    $[24] = onChange;
    $[25] = open;
    $[26] = options;
    $[27] = value;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== t13 || $[30] !== t14 || $[31] !== t4) {
    t15 = <div className="ff-picker" ref={box} data-block={t4}>{t13}{t14}</div>;
    $[29] = t13;
    $[30] = t14;
    $[31] = t4;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  return t15;
}

/** A one-time code: one box per digit, always left to right; paste and autofill work. */
function _temp(v) {
  return !v;
}
export function CodeBoxes(t0) {
  const $ = _c(10);
  const {
    id,
    value,
    onChange,
    length: t1,
    disabled,
    invalid,
    pasteTransformer
  } = t0;
  const length = t1 === undefined ? 5 : t1;
  const auto = useId();
  const t2 = invalid || undefined;
  const t3 = id ?? auto;
  let t4;
  if ($[0] !== disabled || $[1] !== length || $[2] !== onChange || $[3] !== pasteTransformer || $[4] !== t3 || $[5] !== value) {
    t4 = <OTPInput id={t3} maxLength={length} value={value} onChange={onChange} disabled={disabled} pasteTransformer={pasteTransformer} inputMode="numeric" autoComplete="one-time-code" containerClassName="ff-code-row" render={_temp3} />;
    $[0] = disabled;
    $[1] = length;
    $[2] = onChange;
    $[3] = pasteTransformer;
    $[4] = t3;
    $[5] = value;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t2 || $[8] !== t4) {
    t5 = <div dir="ltr" className="ff-code" data-invalid={t2}>{t4}</div>;
    $[7] = t2;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}
function _temp3(t0) {
  const {
    slots
  } = t0;
  return <>{slots.map(_temp2)}</>;
}
function _temp2(s, i) {
  return <div key={i} className="ff-code-box" data-active={s.isActive || undefined}>{s.char ?? (s.hasFakeCaret ? <span className="ff-caret" /> : null)}</div>;
}